import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Translate as TranslateIcon,
  PlayArrow as PlayIcon,
  Favorite as FavoriteIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import BulkTextGenerationDialog from "../components/BulkTextGenerationDialog";
import CreateTextDialog from "../components/CreateTextDialog";
import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchAdminTexts,
  deleteAdminText,
  fetchTextLanguages,
  bulkDeleteTexts,
  bulkHardDeleteTexts,
} from "../store/slices/adminTextsSlice";

const DIFFICULTY_LEVELS = [
  { value: "BEGINNER", label: "Beginner", color: "success" },
  { value: "ELEMENTARY", label: "Elementary", color: "info" },
  { value: "INTERMEDIATE", label: "Intermediate", color: "warning" },
  {
    value: "UPPER_INTERMEDIATE",
    label: "Upper Intermediate",
    color: "warning",
  },
  { value: "ADVANCED", label: "Advanced", color: "error" },
  { value: "PROFICIENT", label: "Proficient", color: "error" },
];

const TextsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { texts, languages, loading, error, pagination } = useAppSelector(
    (state) => state.adminTexts,
  );

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [accessFilter, setAccessFilter] = useState("");
  const [moderationFilter, setModerationFilter] = useState("");
  const [authorFilter, setAuthorFilter] = useState("");

  // UI states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkGenerationDialogOpen, setBulkGenerationDialogOpen] =
    useState(false);
  const [selectedTexts, setSelectedTexts] = useState<string[]>([]);
  const [bulkActionDialog, setBulkActionDialog] = useState<{
    open: boolean;
    action: "delete" | "translate" | "access" | null;
  }>({ open: false, action: null });
  const [massDeleteDialog, setMassDeleteDialog] = useState<{
    open: boolean;
    phase: "idle" | "confirm";
    count?: number;
    loading: boolean;
    error?: string;
    confirmPhrase: string;
  }>({ open: false, phase: "idle", loading: false, confirmPhrase: "" });
  const [hardDeleteDialog, setHardDeleteDialog] = useState<{
    open: boolean;
    phase: "idle" | "confirm";
    count?: number;
    loading: boolean;
    error?: string;
    confirmPhrase: string;
    includeLogs: boolean;
  }>({
    open: false,
    phase: "idle",
    loading: false,
    confirmPhrase: "",
    includeLogs: true,
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  useEffect(() => {
    // Load available languages on component mount
    dispatch(fetchTextLanguages());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchAdminTexts({
        page,
        limit,
        search: searchTerm || undefined,
        language: languageFilter || undefined,
        difficulty: difficultyFilter || undefined,
        access: accessFilter || undefined,
        moderation: moderationFilter || undefined,
        author: authorFilter || undefined,
      }),
    );
  }, [
    dispatch,
    page,
    limit,
    searchTerm,
    languageFilter,
    difficultyFilter,
    accessFilter,
    moderationFilter,
    authorFilter,
  ]);

  const handleDelete = async (textId: string): Promise<void> => {
    if (window.confirm("Are you sure you want to delete this text?")) {
      await dispatch(deleteAdminText(textId));
      // Refresh the list
      dispatch(
        fetchAdminTexts({
          page,
          limit,
          search: searchTerm || undefined,
          language: languageFilter || undefined,
          difficulty: difficultyFilter || undefined,
          access: accessFilter || undefined,
          moderation: moderationFilter || undefined,
          author: authorFilter || undefined,
        }),
      );
    }
  };

  const handleView = (textId: string): void => {
    navigate(`/texts/${textId}`);
  };

  const handleEdit = (textId: string): void => {
    navigate(`/texts/${textId}?edit=true`);
  };

  const handleSelectText = (textId: string): void => {
    setSelectedTexts((prev) =>
      prev.includes(textId)
        ? prev.filter((id) => id !== textId)
        : [...prev, textId],
    );
  };

  const handleSelectAll = (): void => {
    setSelectedTexts(
      selectedTexts.length === texts.length ? [] : texts.map((text) => text.id),
    );
  };

  const handleBulkAction = (
    action: "delete" | "translate" | "access",
  ): void => {
    setBulkActionDialog({ open: true, action });
  };

  const executeBulkAction = async (): Promise<void> => {
    const { action } = bulkActionDialog;

    switch (action) {
      case "delete":
        if (window.confirm(`Delete ${selectedTexts.length} selected texts?`)) {
          for (const textId of selectedTexts) {
            await dispatch(deleteAdminText(textId));
          }
          setSelectedTexts([]);
        }
        break;
      case "translate":
        // Open bulk translation dialog
        break;
      case "access":
        // Open bulk access update dialog
        break;
    }

    setBulkActionDialog({ open: false, action: null });
    // Refresh data
    dispatch(
      fetchAdminTexts({
        page,
        limit,
        search: searchTerm || undefined,
        language: languageFilter || undefined,
        difficulty: difficultyFilter || undefined,
        access: accessFilter || undefined,
        moderation: moderationFilter || undefined,
        author: authorFilter || undefined,
      }),
    );
  };

  const getDifficultyInfo = (
    difficulty: string | number,
  ): { value: string; label: string; color: string } => {
    const difficultyStr = String(difficulty);
    return (
      DIFFICULTY_LEVELS.find((d) => d.value === difficultyStr) || {
        value: difficultyStr,
        label: difficultyStr,
        color: "default",
      }
    );
  };

  type ChipColor =
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";

  const allowedChipColors: readonly ChipColor[] = [
    "default",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ] as const;

  const mapDifficultyColor = (color: string): ChipColor => {
    return (allowedChipColors as readonly string[]).includes(color)
      ? (color as ChipColor)
      : "default";
  };

  return (
    <Box>
      {/* Header with Stats */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4">Text management</Typography>
          <Typography variant="body2" color="textSecondary">
            {pagination ? `Total texts: ${pagination.total}` : "Loading..."}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={async () => {
              setMassDeleteDialog({
                open: true,
                phase: "idle",
                loading: true,
                confirmPhrase: "",
              });
              try {
                const resp = await dispatch(
                  bulkDeleteTexts({
                    filters: {
                      search: searchTerm || undefined,
                      language: languageFilter || undefined,
                      difficulty: difficultyFilter || undefined,
                      access: accessFilter || undefined,
                      moderation: moderationFilter || undefined,
                      author: authorFilter || undefined,
                    },
                    dryRun: true,
                  }),
                );
                if (bulkDeleteTexts.fulfilled.match(resp)) {
                  setMassDeleteDialog((prev) => ({
                    ...prev,
                    loading: false,
                    count: resp.payload.count,
                    phase: "confirm",
                  }));
                } else if (bulkDeleteTexts.rejected.match(resp)) {
                  setMassDeleteDialog((prev) => ({
                    ...prev,
                    loading: false,
                    error: resp.payload || "Bulk delete dry run failed",
                    phase: "idle",
                  }));
                }
              } catch (e) {
                setMassDeleteDialog((prev) => ({
                  ...prev,
                  loading: false,
                  error: e instanceof Error ? e.message : "Unexpected error",
                  phase: "idle",
                }));
              }
            }}
          >
            Delete by filter
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={async () => {
              setHardDeleteDialog({
                open: true,
                phase: "idle",
                loading: true,
                confirmPhrase: "",
                includeLogs: true,
              });
              try {
                const dryRunData = {
                  filters: {
                    search: searchTerm || undefined,
                    language: languageFilter || undefined,
                    difficulty: difficultyFilter || undefined,
                    access: accessFilter || undefined,
                    moderation: moderationFilter || undefined,
                    author: authorFilter || undefined,
                  },
                  dryRun: true,
                  includeLogs: true,
                };
                const resp = await dispatch(bulkHardDeleteTexts(dryRunData));
                if (bulkHardDeleteTexts.fulfilled.match(resp)) {
                  setHardDeleteDialog((prev) => ({
                    ...prev,
                    loading: false,
                    count: resp.payload.count,
                    phase: "confirm",
                  }));
                } else if (bulkHardDeleteTexts.rejected.match(resp)) {
                  setHardDeleteDialog((prev) => ({
                    ...prev,
                    loading: false,
                    error: resp.payload || "Hard delete dry run failed",
                    phase: "idle",
                  }));
                }
              } catch (e) {
                setHardDeleteDialog((prev) => ({
                  ...prev,
                  loading: false,
                  error: e instanceof Error ? e.message : "Unexpected error",
                  phase: "idle",
                }));
              }
            }}
          >
            Permanently delete
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setBulkGenerationDialogOpen(true)}
          >
            Bulk generation
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create text
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4">{pagination?.total || 0}</Typography>
              <Typography variant="body2" color="textSecondary">
                Total texts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4">{languages.length}</Typography>
              <Typography variant="body2" color="textSecondary">
                Languages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4">
                {texts.filter((t) => t.freeUserAccess).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Free
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4">
                {texts.reduce(
                  (sum, t) => sum + (t.translations?.length || 0),
                  0,
                )}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Translations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Translation Recommendations & Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        {/* Translation Status Summary */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Translation management</Typography>
            {!loading && (
              <Box display="flex" gap={1}>
                <Chip
                  size="small"
                  label={`${texts.reduce((sum, t) => sum + (t.translations?.length || 0), 0)} translations`}
                  color="warning"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`${languages.length} languages`}
                  color="info"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<TranslateIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="small"
          >
            Bulk generation
          </Button>
        </Box>

        {/* Filters */}
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by content and translations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Language</InputLabel>
              <Select
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
                label="Language"
              >
                <MenuItem value="">All languages</MenuItem>
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name} ({lang.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                label="Difficulty"
              >
                <MenuItem value="">All levels</MenuItem>
                {DIFFICULTY_LEVELS.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Access</InputLabel>
              <Select
                value={accessFilter}
                onChange={(e) => setAccessFilter(e.target.value)}
                label="Access"
              >
                <MenuItem value="">Any access</MenuItem>
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Author</InputLabel>
              <Select
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
                label="Author"
              >
                <MenuItem value="">All authors</MenuItem>
                <MenuItem value="system">System</MenuItem>
                <MenuItem value="user">Users</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Moderation</InputLabel>
              <Select
                value={moderationFilter}
                onChange={(e) => setModerationFilter(e.target.value)}
                label="Moderation"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Bulk Actions */}
        {selectedTexts.length > 0 && (
          <Box
            sx={{ mt: 2, p: 2, bgcolor: "action.selected", borderRadius: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" fontWeight="medium">
                Selected texts: {selectedTexts.length}
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  startIcon={<TranslateIcon />}
                  onClick={() => handleBulkAction("translate")}
                  variant="outlined"
                >
                  Generate translations
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleBulkAction("access")}
                  variant="outlined"
                >
                  Change access
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleBulkAction("delete")}
                  variant="outlined"
                >
                  Delete
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      selectedTexts.length === texts.length && texts.length > 0
                    }
                    indeterminate={
                      selectedTexts.length > 0 &&
                      selectedTexts.length < texts.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Statistics</TableCell>
                <TableCell>Translations</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : texts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No texts to display
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                texts.map((text) => {
                  const difficultyInfo = getDifficultyInfo(
                    text.difficulty || text.level,
                  );
                  return (
                    <TableRow key={text.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedTexts.includes(text.id)}
                          onChange={() => handleSelectText(text.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mb: 0.5 }}
                          >
                            {text.topic || "Untitled"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ display: "block" }}
                          >
                            {text.content.length > 80
                              ? `${text.content.substring(0, 80)}...`
                              : text.content}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={text.language.toUpperCase()}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={difficultyInfo.label}
                          size="small"
                          color={mapDifficultyColor(difficultyInfo.color)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {text.author ? text.author.email : "System"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={text.freeUserAccess ? "Free" : "Premium"}
                          size="small"
                          color={text.freeUserAccess ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Plays">
                            <Chip
                              icon={<PlayIcon />}
                              label={text._count?.progress || 0}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                          <Tooltip title="Favorited">
                            <Chip
                              icon={<FavoriteIcon />}
                              label={text._count?.favorites || 0}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={text.translations?.length || 0}
                          size="small"
                          color={
                            text.translations?.length &&
                            text.translations.length > 0
                              ? "success"
                              : "default"
                          }
                          variant={
                            text.translations?.length &&
                            text.translations.length > 0
                              ? "filled"
                              : "outlined"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(text.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleView(text.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(text.id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(text.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
          <Button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Typography sx={{ mx: 2, alignSelf: "center" }}>
            Page {page} of {pagination.totalPages}
          </Typography>
          <Button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(page + 1)}
            sx={{ ml: 1 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Create Text Dialog */}
      <CreateTextDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      {/* Bulk Text Generation Dialog */}
      <BulkTextGenerationDialog
        open={bulkGenerationDialogOpen}
        onClose={() => setBulkGenerationDialogOpen(false)}
      />

      {/* Bulk Action Confirmation Dialog */}
      <Dialog
        open={bulkActionDialog.open}
        onClose={() => setBulkActionDialog({ open: false, action: null })}
      >
        <DialogTitle>Confirm action</DialogTitle>
        <DialogContent>
          <Typography>
            {bulkActionDialog.action === "delete" &&
              `Are you sure you want to delete ${selectedTexts.length} selected texts?`}
            {bulkActionDialog.action === "translate" &&
              `Generate translations for ${selectedTexts.length} selected texts?`}
            {bulkActionDialog.action === "access" &&
              `Update access settings for ${selectedTexts.length} selected texts?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkActionDialog({ open: false, action: null })}
          >
            Cancel
          </Button>
          <Button onClick={executeBulkAction} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mass Delete Dialog */}
      <Dialog
        open={massDeleteDialog.open}
        onClose={() =>
          setMassDeleteDialog({
            open: false,
            phase: "idle",
            loading: false,
            confirmPhrase: "",
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk text deletion</DialogTitle>
        <DialogContent>
          {massDeleteDialog.loading && (
            <Box display="flex" alignItems="center" gap={2} py={2}>
              <CircularProgress size={20} />{" "}
              <Typography>Counting...</Typography>
            </Box>
          )}
          {!massDeleteDialog.loading && massDeleteDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {massDeleteDialog.error}
            </Alert>
          )}
          {!massDeleteDialog.loading &&
            massDeleteDialog.phase === "confirm" && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography>
                  All texts matching the current filters will be marked as
                  deleted (soft delete), matching the current filters.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Found: <b>{massDeleteDialog.count}</b> records.
                </Typography>
                <Alert
                  severity={
                    massDeleteDialog.count && massDeleteDialog.count > 500
                      ? "warning"
                      : "info"
                  }
                >
                  The operation is irreversible via UI. A soft delete will be
                  performed (isDeleted=true). Full deletion requires admin SQL.
                </Alert>
                <Typography variant="caption" color="textSecondary">
                  To delete everything without filters, type DELETE_ALL_TEXTS.
                  For deletions with filters, just type DELETE.
                </Typography>
                <TextField
                  label="Confirmation"
                  placeholder={(() => {
                    const hasFilters = Boolean(
                      (searchTerm && searchTerm.trim()) ||
                        (languageFilter && languageFilter.trim()) ||
                        (difficultyFilter && difficultyFilter.trim()) ||
                        (accessFilter && accessFilter.trim()) ||
                        (moderationFilter && moderationFilter.trim()) ||
                        (authorFilter && authorFilter.trim()),
                    );
                    return hasFilters ? "DELETE" : "DELETE_ALL_TEXTS";
                  })()}
                  value={massDeleteDialog.confirmPhrase}
                  onChange={(e) =>
                    setMassDeleteDialog((prev) => ({
                      ...prev,
                      confirmPhrase: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setMassDeleteDialog({
                open: false,
                phase: "idle",
                loading: false,
                confirmPhrase: "",
              })
            }
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={
              massDeleteDialog.loading ||
              (massDeleteDialog.phase === "confirm" &&
                (() => {
                  // Проверяем наличие фильтров так же, как это делает бэкенд
                  const hasFilters = Boolean(
                    (searchTerm && searchTerm.trim()) ||
                      (languageFilter && languageFilter.trim()) ||
                      (difficultyFilter && difficultyFilter.trim()) ||
                      (accessFilter && accessFilter.trim()) ||
                      (moderationFilter && moderationFilter.trim()) ||
                      (authorFilter && authorFilter.trim()),
                  );
                  return hasFilters
                    ? massDeleteDialog.confirmPhrase !== "DELETE"
                    : massDeleteDialog.confirmPhrase !== "DELETE_ALL_TEXTS";
                })())
            }
            onClick={async () => {
              setMassDeleteDialog((prev) => ({ ...prev, loading: true }));
              const requestData = {
                filters: {
                  search: searchTerm || undefined,
                  language: languageFilter || undefined,
                  difficulty: difficultyFilter || undefined,
                  access: accessFilter || undefined,
                  moderation: moderationFilter || undefined,
                  author: authorFilter || undefined,
                },
                dryRun: false,
                confirmPhrase: massDeleteDialog.confirmPhrase,
              };
              const resp = await dispatch(bulkDeleteTexts(requestData));
              setMassDeleteDialog((prev) => ({ ...prev, loading: false }));
              if (bulkDeleteTexts.fulfilled.match(resp)) {
                setMassDeleteDialog({
                  open: false,
                  phase: "idle",
                  loading: false,
                  confirmPhrase: "",
                });
                // Обновляем список
                dispatch(
                  fetchAdminTexts({
                    page: 1,
                    limit,
                    search: searchTerm || undefined,
                    language: languageFilter || undefined,
                    difficulty: difficultyFilter || undefined,
                    access: accessFilter || undefined,
                    moderation: moderationFilter || undefined,
                    author: authorFilter || undefined,
                  }),
                );
              } else {
                setMassDeleteDialog((prev) => ({
                  ...prev,
                  error: resp.payload,
                }));
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hard Delete Dialog */}
      <Dialog
        open={hardDeleteDialog.open}
        onClose={() =>
          setHardDeleteDialog({
            open: false,
            phase: "idle",
            loading: false,
            confirmPhrase: "",
            includeLogs: true,
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Permanent text deletion</DialogTitle>
        <DialogContent>
          {hardDeleteDialog.loading && (
            <Box display="flex" alignItems="center" gap={2} py={2}>
              <CircularProgress size={20} />{" "}
              <Typography>Counting...</Typography>
            </Box>
          )}
          {!hardDeleteDialog.loading && hardDeleteDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {hardDeleteDialog.error}
            </Alert>
          )}
          {!hardDeleteDialog.loading &&
            hardDeleteDialog.phase === "confirm" && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography>
                  Records will be PERMANENTLY deleted (hard delete). Cascade
                  will remove translations / progress / favorites. Generation
                  logs can also be deleted.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Found: <b>{hardDeleteDialog.count}</b> texts.
                </Typography>
                <Alert severity="error">
                  The operation is irreversible. A backup is recommended before
                  running it.
                </Alert>
                <Box display="flex" alignItems="center" gap={1}>
                  <Checkbox
                    size="small"
                    checked={hardDeleteDialog.includeLogs}
                    onChange={(e) =>
                      setHardDeleteDialog((prev) => ({
                        ...prev,
                        includeLogs: e.target.checked,
                      }))
                    }
                  />
                  <Typography variant="body2">
                    Delete related generation logs
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Without filters you must type HARD_DELETE_ALL_TEXTS. With
                  filters — HARD_DELETE.
                </Typography>
                <TextField
                  label="Confirmation"
                  placeholder={(() => {
                    const hasFilters = Boolean(
                      (searchTerm && searchTerm.trim()) ||
                        (languageFilter && languageFilter.trim()) ||
                        (difficultyFilter && difficultyFilter.trim()) ||
                        (accessFilter && accessFilter.trim()) ||
                        (moderationFilter && moderationFilter.trim()) ||
                        (authorFilter && authorFilter.trim()),
                    );
                    return hasFilters ? "HARD_DELETE" : "HARD_DELETE_ALL_TEXTS";
                  })()}
                  value={hardDeleteDialog.confirmPhrase}
                  onChange={(e) =>
                    setHardDeleteDialog((prev) => ({
                      ...prev,
                      confirmPhrase: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setHardDeleteDialog({
                open: false,
                phase: "idle",
                loading: false,
                confirmPhrase: "",
                includeLogs: true,
              })
            }
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={
              hardDeleteDialog.loading ||
              (hardDeleteDialog.phase === "confirm" &&
                (() => {
                  // Проверяем наличие фильтров так же, как это делает бэкенд
                  const hasFilters = Boolean(
                    (searchTerm && searchTerm.trim()) ||
                      (languageFilter && languageFilter.trim()) ||
                      (difficultyFilter && difficultyFilter.trim()) ||
                      (accessFilter && accessFilter.trim()) ||
                      (moderationFilter && moderationFilter.trim()) ||
                      (authorFilter && authorFilter.trim()),
                  );
                  // Validation logic retains dynamic expected phrase
                  return hasFilters
                    ? hardDeleteDialog.confirmPhrase !== "HARD_DELETE"
                    : hardDeleteDialog.confirmPhrase !==
                        "HARD_DELETE_ALL_TEXTS";
                })())
            }
            onClick={async () => {
              setHardDeleteDialog((prev) => ({ ...prev, loading: true }));
              const requestData = {
                filters: {
                  search: searchTerm || undefined,
                  language: languageFilter || undefined,
                  difficulty: difficultyFilter || undefined,
                  access: accessFilter || undefined,
                  moderation: moderationFilter || undefined,
                  author: authorFilter || undefined,
                },
                dryRun: false,
                confirmPhrase: hardDeleteDialog.confirmPhrase,
                includeLogs: hardDeleteDialog.includeLogs,
              };
              const resp = await dispatch(bulkHardDeleteTexts(requestData));
              setHardDeleteDialog((prev) => ({ ...prev, loading: false }));
              if (bulkHardDeleteTexts.fulfilled.match(resp)) {
                setHardDeleteDialog({
                  open: false,
                  phase: "idle",
                  loading: false,
                  confirmPhrase: "",
                  includeLogs: true,
                });
                dispatch(
                  fetchAdminTexts({
                    page: 1,
                    limit,
                    search: searchTerm || undefined,
                    language: languageFilter || undefined,
                    difficulty: difficultyFilter || undefined,
                    access: accessFilter || undefined,
                    moderation: moderationFilter || undefined,
                    author: authorFilter || undefined,
                  }),
                );
              } else {
                setHardDeleteDialog((prev) => ({
                  ...prev,
                  error: resp.payload,
                }));
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TextsPage;
